import cx from "classnames";
import { memo, ReactNode } from "react";
import { Checkbox, CheckboxProps } from "react-aria-components";

import { FlexAlign } from "types/Flex";

import styles from "./styles.module.css";
import ToggleContent from "./Content";
import { TypographyProps } from "../Typography";

type ToggleProps = CheckboxProps & {
  id?: string;
  color?: "default" | "inversed";
  variant: "checkbox" | "switch";
  className?: string;
  children?: ReactNode;
  align?: FlexAlign;
  dataTestId?: string;
  tooltip?: (isSelected: boolean) => string;
  initialMinHeight?: boolean;
  typographyVariant?: TypographyProps["variant"];
};

/**
 * - Checkbox variant
 *   - Used within forms to indicate boolean state of form fields, requires confirmation by saving the form
 * - Switch variant
 *   - Used to trigger immediate interaction (toggling states)
 *   - Used in forms to hide/show form sections
 */
const Toggle = ({
  children,
  className,
  color,
  align,
  variant,
  tooltip,
  dataTestId,
  initialMinHeight,
  typographyVariant,
  ...rest
}: ToggleProps) => {
  // When no aria-label is provided and there are no children to act as a label,
  // we need to provide a default aria-label for screen readers
  const toggleProps = {
    ...rest,
    "aria-label":
      rest["aria-label"] || (children ? undefined : `Toggle ${rest.isSelected ? "on" : "off"}`),
  };

  return (
    <Checkbox
      {...toggleProps}
      className={cx(styles.toggleWrapper, className)}
      data-indeterminate={rest.isIndeterminate}
    >
      {({ isDisabled, isIndeterminate, isSelected }) => (
        <ToggleContent
          color={color}
          align={align}
          variant={variant}
          isDisabled={isDisabled}
          isIndeterminate={isIndeterminate}
          isSelected={isSelected}
          tooltip={tooltip?.(isSelected || isIndeterminate)}
          dataTestId={dataTestId}
          initialMinHeight={initialMinHeight}
          typographyVariant={typographyVariant}
        >
          {children}
        </ToggleContent>
      )}
    </Checkbox>
  );
};

Toggle.displayName = "DS.Toggle";

export default memo(Toggle);
